import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// AccountChip.tsx -- top-right avatar + multi-account dropdown.
//
// Lets the developer log in as multiple users and swap between them
// without going through logout/login. Gigbook's pattern, adapted for
// fdn-app's cookie-or-Bearer auth (utils/accounts.ts is the
// localStorage layer; api.ts adds Authorization: Bearer when set).
//
// Click the avatar -> dropdown with:
//   - the active user's profile chip (read-only)
//   - list of saved accounts; click to switch
//   - "Add account" -> /login?addAccount=1 (preserves saved accounts)
//   - "Logout current" -> remove just the active account, fall back
//     to next saved or to /login
//   - "Logout all" -> wipe every account, redirect to /login
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { isDndActive, setDndActive } from '../utils/notify.js';
export function AccountChip() {
    const { user, accounts, switchAccount, logout, logoutAll } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    // Outside-click + Escape close.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    if (!user)
        return null;
    const initials = getInitials(user.name || user.email);
    const handleSwitch = async (accountId) => {
        if (accountId === user.userId) {
            setOpen(false);
            return;
        }
        setOpen(false);
        try {
            await switchAccount(accountId);
            // Navigating to /orgs forces ProjectDesktop to re-resolve the
            // tenant credentials for the new user; otherwise the still-
            // mounted desktop keeps the old project context.
            navigate('/orgs');
        }
        catch {
            navigate('/login');
        }
    };
    const handleAdd = () => {
        setOpen(false);
        navigate('/login?addAccount=1');
    };
    const handleLogout = async () => {
        setOpen(false);
        await logout();
        navigate('/orgs');
    };
    const handleLogoutAll = async () => {
        setOpen(false);
        await logoutAll();
        navigate('/login');
    };
    return (_jsxs("div", { ref: ref, className: "account-chip", children: [_jsx("button", { type: "button", className: "account-chip-button", onClick: () => setOpen((o) => !o), title: user.name || user.email, "aria-label": `Active account: ${user.name || user.email}`, "aria-expanded": open, children: _jsx("span", { className: "account-chip-avatar", children: initials }) }), open && (_jsxs("div", { className: "account-chip-menu", role: "menu", children: [_jsxs("div", { className: "account-chip-current", children: [_jsx("div", { className: "account-chip-current-name", children: user.name || 'Unnamed' }), _jsx("div", { className: "account-chip-current-email", children: user.email })] }), accounts.length > 1 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "account-chip-divider" }), _jsx("div", { className: "account-chip-section-label", children: "Switch to" }), accounts.map((a) => {
                                const isCurrent = a.accountId === user.userId;
                                return (_jsxs("button", { type: "button", className: "account-chip-row", onClick: () => void handleSwitch(a.accountId), disabled: isCurrent, role: "menuitem", children: [_jsx("span", { className: "account-chip-row-avatar", children: getInitials(a.name || a.email) }), _jsxs("span", { className: "account-chip-row-text", children: [_jsx("span", { className: "account-chip-row-name", children: a.name || a.email }), _jsx("span", { className: "account-chip-row-sub", children: a.email })] }), isCurrent && _jsx("span", { className: "account-chip-current-badge", children: "Current" })] }, a.accountId));
                            })] })), _jsx("div", { className: "account-chip-divider" }), _jsx(DndToggleRow, {}), _jsx("div", { className: "account-chip-divider" }), _jsx("button", { type: "button", className: "account-chip-action", onClick: handleAdd, role: "menuitem", children: '+ Add account' }), _jsx("button", { type: "button", className: "account-chip-action", onClick: () => void handleLogout(), role: "menuitem", children: "Logout current" }), accounts.length > 0 && (_jsx("button", { type: "button", className: "account-chip-action danger", onClick: () => void handleLogoutAll(), role: "menuitem", children: "Logout all accounts" }))] }))] }));
}
/**
 * Tiny stateful row that exposes the DND boolean as a checkbox-style
 * toggle. The MembersDock reads isDndActive() at notification time;
 * flipping it here changes the answer for the next incoming message.
 *
 * v1 stores the flag in localStorage. v2 will publish DND through
 * the activity-status token (workbench C.4) so peers see "Alice is
 * on DND" without polling auth-state. utils/notify.ts already
 * abstracts the storage so swapping to the token is a one-file
 * change later.
 */
function DndToggleRow() {
    const [active, setActive] = useState(() => isDndActive());
    const onToggle = () => {
        const next = !active;
        setDndActive(next);
        setActive(next);
    };
    return (_jsxs("button", { type: "button", className: "account-chip-action account-chip-dnd", onClick: onToggle, role: "menuitem", "aria-pressed": active, title: active
            ? 'Do Not Disturb is ON. Incoming sounds + auto-popups are suppressed.'
            : 'Click to enable Do Not Disturb. Suppresses incoming sounds + auto-popups until turned off.', children: [_jsx("span", { className: "account-chip-dnd-glyph", "aria-hidden": true, children: active ? '\uD83D\uDD15' : '\uD83D\uDD14' }), _jsx("span", { className: "account-chip-dnd-text", children: active ? 'Do Not Disturb (on)' : 'Do Not Disturb' })] }));
}
function getInitials(s) {
    if (!s)
        return '?';
    const parts = s.trim().split(/\s+/);
    if (parts.length === 1) {
        return s.slice(0, 2).toUpperCase();
    }
    return (parts[0][0] ?? '?').toUpperCase() + (parts[parts.length - 1][0] ?? '').toUpperCase();
}
